const saleCalculator = require('../../../../sales/saleCalculator');
const dateUtil = require('../../../../../dateUtil/dateUtil');
const constants = require('../../../../constants');
const logger = require('../../../../../logger/logger');
const localizationUtils = require('../../../../localizationUtils');
const pakistanEndorsementUtil = require('../pakistanEndorsementUtil');


//#region Documentation

//
// SRB = Sindh Revenue Board, Govt of Sindh, Pakistan
// endorsement handler for Pakistan SRB saleEndorsement integration
// applySaleEndorsement = add endorsement to sale.endorsements
// applyReceiptEndorsement = add endorsement related print lines to receipt
//

// SRB Test Environment:
// SRB test environment works by setting TransType=Test in the invoice submitted to SDC. There are no separate test codes as such
// but their technical help line will provide you with test POSID/Access Code on request.
// 

//#endregion


export default {
    applySaleEndorsement: applySaleEndorsement,
    getSaleEndorsement: getSaleEndorsement
}

async function applySaleEndorsement(sale, integration)
{
    if (!pakistanEndorsementUtil.isSaleEndorsementApplicable(sale, integration))
        return;

    if (integration.jsonData.isDeferred && canDefer(sale)) 
    {
        sale.endorsements.push({
            integrationId: integration.id,
            integrationType: integration.integrationType,
            externalEntity: integration.externalEntity,
            isDeferred: true
        });
    }
    else
    {
        const endorsement = await getSaleEndorsement(sale, integration);

        if (endorsement != null)
        {
            sale.endorsements.push(endorsement);
        }
    }
}

function canDefer (sale)
{
    return sale.paymentMethod === constants.PaymentMethodCash;
}

async function getSaleEndorsement(sale, integration) 
{
    // Returns SRB sale endorsement

    const serviceUrl = integration.jsonData.serviceUrl;     // SRB SDC serviceUrl
    const srbInvoice = getSRBCompliantSaleInvoice(integration, sale);

    logger.info(`SRB: getSaleEndorsement request - propertyId=${sale.propertyId}; SaleId/Number=${sale.id || 'new sale'}/'${sale.number}'; SRB Invoice = ${JSON.stringify(srbInvoice)}`);

    const request = {
        method: "POST",
        headers: getHttpHeaders(),
        body: JSON.stringify(srbInvoice)
    };

    const response = await fetch(serviceUrl, request);

    if (response.status < 200 || response.status > 299)
    {
        let text = await response.text();
        text = text || `Failed to get SRB endorsement with response status = ${response.status}`;
        logger.error(`SRB: getSaleEndorsement http status error: ${text} `);
        throw new Error(text);
    }

    const responseData = await response.json();

    const endorsement = {
        integrationId: integration.id,
        integrationType: integration.integrationType,
        externalEntity: integration.externalEntity,
        responseData: responseData,
        isError: responseData.resCode !== "00",
    }

    if (endorsement.isError) 
    {
        logger.error(`SRB: getSaleEndorsement response - ${JSON.stringify(responseData)}`);
    }
    else
    {
        logger.info(`SRB: getSaleEndorsement response - ${JSON.stringify(responseData)}`);
    }

    return endorsement;

    //#region Sample responseData from SRB SDC Service

    // Note following are sample data from test environment and not from prod environment

    /*

    Sample successful responseData::
    {
        "srbInvoceId": "15520220818165853",
        "resCode": "00"
    }

    Sample error responseData:
    {
        "err": "Sales Invoice not entered due to invalid data.",
        "resCode": "12"
    }

    */

    //#endregion
}

function getSRBCompliantSaleInvoice(integration, sale)
{
    let totalSaleValue = 0;         // total sale amount exclusive of tax
    let totalTaxCharged = 0;        // total tax amount

    const isVatInclusive = localizationUtils.isVatInclusive();

    (function processSaleItems(saleItems)
    {
        for (const saleItem of saleItems)
        {
            // Ignore saleItems where unitPrice = 0

            if (saleItem.unitPrice > constants.numericThresholdValue) 
            {
                let saleValue = 0, taxCharged = 0, totalAmount = 0;

                taxCharged = saleCalculator.roundNumericValue(saleItem.quantity * saleItem.vat);

                if (isVatInclusive) 
                {
                    totalAmount = saleCalculator.roundNumericValue(saleItem.quantity * saleItem.unitPrice);
                    saleValue = saleCalculator.roundNumericValue(totalAmount - taxCharged);
                }
                else
                {
                    saleValue = saleCalculator.roundNumericValue(saleItem.quantity * saleItem.unitPrice);
                    totalAmount = saleValue + taxCharged;
                }

                totalSaleValue += saleValue;
                totalTaxCharged += taxCharged;
            }

            if (saleItem.saleItems.length > 0)
            {
                processSaleItems(saleItem.saleItems);   // Recursion
            }
        }
    })(sale.saleItems);     // IIF

    const totalCharges = saleCalculator.calculateTotalCharges(sale);     // No separate field for charges in SBR invoice
    totalTaxCharged += saleCalculator.calculateTotalChargesVAT(sale);    // vat on charges

    const totalRefunds = saleCalculator.calculateTotalRefunds(sale);     // No field for refund in SRB invoice
    const totalDiscount = saleCalculator.calculateTotalDiscounts(sale);  // No field for discount in SRB invoice

    // Unlike FBR integration, we are not using totalCharges, totalRefunds and totalDiscount in SRB integration
    // and this needs to be checked with SRB

    const invoice = {

        posId: integration.jsonData.posId,          // SRB Generated POS Station ID
        name: integration.jsonData.name,            // Business Name
        ntn: integration.jsonData.ntn,              // Business NTN Number
        TransType: integration.jsonData.TransType,  // "Test" for testing, "Live" for production

        invoiceDateTime: dateUtil.convertToLocalStandard(sale.dateTime),
        invoiceID: sale.number,
        invoiceType: 1,                         // This probably means new invoice ?

        rateValue: "N/A",                       // rateValue is incalculable ?
        saleValue: saleCalculator.roundNumericValue(totalSaleValue),
        taxAmount: saleCalculator.roundNumericValue(totalTaxCharged),
        consumerName: "N/A",
        consumerNTN: "N/A",
        address: "N/A",
        tariffCode: "N/A",
        extraInf: "N/A",
    }

    return invoice;
}

function getHttpHeaders()
{
    return ({
        // "Content-Type": "application/json",  // triggers pre-flight resulting in CORS error
        "Content-Type": "text/plain",
    });
}
